import { useMutation } from '@apollo/client'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { navigateToExternalLink } from '@deltek/specification-client/core/Utilities/urlUtils'
import ProductListingCardDocuments, {
    ProductListingCardDocumentsProps,
} from '@deltek/specification-client/core/Widgets/ProductListing/Card/ProductListingCardDocuments'
import ProductListingManufacturerOptionItem from '@deltek/specification-client/core/Widgets/ProductListing/Card/ProductListingManufacturerOptionItem'
import { makeStyles, Theme } from '@material-ui/core'
import { GenerateProductSpecification } from 'bpm/api/graphQL/mutations/productListing.mutation'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { useEffect } from 'react'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import ProductListingCardType from './ProductListingCardType'

const useStyles = makeStyles((theme: Theme) => ({
    cardFooter: {
        backgroundColor: '#5E5F61',
        height: 40,
    },
    guttered: {
        padding: theme.spacing(1.2),
        paddingRight: theme.spacing(1.8),
    },
    loadingSkeleton: {
        minWidth: 80,
        height: 20,
        backgroundColor: 'rgba(0,0,0,0.2)',
    },
    cardIcons: {
        marginRight: theme.spacing(2),
    },
    buttonContainer: {
        width: 'max-content',
    },
}))

const getListingColor = (type: ListingLevel): string => {
    const listingcolor: Record<ListingLevel, string> = {
        [ListingLevel.PremiumMasterSpec]: '#FBB41F',
        [ListingLevel.PremiumProprietary]: '#FD3431',
        [ListingLevel.Premium]: '#F78909',
        [ListingLevel.Product]: '#C4782A',
        [ListingLevel.Manufacturer]: '#b1b0b1',
        [ListingLevel.ProductListing]: '#b1b0b1',
        [ListingLevel.None]: '#FFFFFF',
    }
    return listingcolor[type] ?? '#FFFFFF'
}
type ProductListingCardActionType = 'Link' | 'Action'
export interface ProductListingCardFooterProps
    extends ProductListingCardDocumentsProps {
    loading?: boolean
    firmName: string
    cardAction?: ProductListingCardActionType
    cardClick?: (event?: React.MouseEvent) => void
    listingId?: string
    showManufacturerOptionItem?: boolean
    hasGBPButton?: boolean
}
export const ProductListingCardFooter: React.FC<ProductListingCardFooterProps> = ({
    listingLevel,
    loading,
    firmName,
    familyName,
    catalogs,
    bimLinks,
    partnershipLinkBIM,
    partnershipLinkSwatchbox,
    manufacturerDocs,
    cardAction,
    cardClick,
    showSpecificationDoc,
    showManufacturerOptionItem,
    listingId,
    hasGBPButton,
}) => {
    const classes = useStyles()

    const [
        generateProductSpecification,
        { data: prodSpecificationData, loading: spfDocLoading },
    ] = useMutation(GenerateProductSpecification)

    const { showGlobalSnackbar } = useGlobalSnackbar()

    const executePSDQuery = (listingId: string) => {
        generateProductSpecification({
            variables: { listingId: listingId },
        })
    }

    const listingLevelExcludedPremiums = React.useMemo(
        () =>
            listingLevel === ListingLevel.Premium ||
            listingLevel === ListingLevel.PremiumMasterSpec ||
            listingLevel === ListingLevel.PremiumProprietary
                ? ListingLevel.None
                : listingLevel,
        [listingLevel]
    )

    useEffect(() => {
        if (
            listingId &&
            showSpecificationDoc &&
            prodSpecificationData?.generateProductSpecification?.downloadURI
        )
            navigateToExternalLink(
                prodSpecificationData?.generateProductSpecification?.downloadURI
            )
        else if (
            listingId &&
            showSpecificationDoc &&
            prodSpecificationData?.generateProductSpecification?.errorMessage
        ) {
            showGlobalSnackbar({
                variables: {
                    message: 'Product Specification Document not Available',
                    variant: 'error',
                    id: `Card_PSD_Error`,
                },
            })
        }
    }, [prodSpecificationData])

    return (
        <GridContainer direction="column">
            <GridContainer
                direction="row"
                item
                className={clsx(
                    classes.cardFooter,
                    classes.guttered,
                    'footerContainer'
                )}
                alignItems="center"
                alignContent="center"
                style={{
                    backgroundColor: getListingColor(
                        processEnv.REACT_APP_EnableBadgelessCards === '1'
                            ? listingLevelExcludedPremiums
                            : listingLevel
                    ),
                }}
                onClick={(event) => cardAction === 'Action' && cardClick?.(event)}
                data-testid="listingCardFooter"
            >
                <GridContainer item xs>
                    <ProductListingCardType
                        loading={loading}
                        variant={
                            processEnv.REACT_APP_EnableBadgelessCards === '1'
                                ? listingLevelExcludedPremiums
                                : listingLevel
                        }
                    />
                </GridContainer>
                <GridContainer
                    item
                    xs="auto"
                    justifyContent="flex-end"
                    className={clsx(classes.buttonContainer, {
                        [classes.cardIcons]: hasGBPButton,
                    })}
                >
                    <ProductListingCardDocuments
                        listingLevel={listingLevel}
                        companyName={firmName}
                        familyName={familyName}
                        catalogs={catalogs}
                        manufacturerDocs={manufacturerDocs}
                        bimLinks={bimLinks}
                        partnershipLinkBIM={partnershipLinkBIM}
                        partnershipLinkSwatchbox={partnershipLinkSwatchbox}
                        executePSDQuery={() => {
                            executePSDQuery(listingId ?? '')
                        }}
                        showSpecificationDoc={showSpecificationDoc}
                        spfDocLoading={spfDocLoading}
                    />
                    {!!showManufacturerOptionItem && (
                        <ProductListingManufacturerOptionItem
                            isBadgelessCardsEnabled={
                                processEnv.REACT_APP_EnableBadgelessCards ===
                                '1'
                            }
                        />
                    )}
                </GridContainer>
            </GridContainer>
        </GridContainer>
    )
}
